<script setup lang="ts">
import { computed } from 'vue'
import { Textarea } from '@/components/shadcn'

type StyleType =
  | 'default'   // 기본 스타일
  | 'error'     // 에러 상태
  | 'success'   // 성공 상태
  | 'warning'   // 경고 상태
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    rows?: number
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    placeholder: '',
    disabled: false,
    readonly: false,
    required: false,
    rows: 3,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string; rows?: number }> = {
  default: { class: '' },
  error: { 
    class: 'border-destructive focus-visible:ring-destructive',
  },
  success: { 
    class: 'border-green-500 focus-visible:ring-green-500',
  },
  warning: { 
    class: 'border-amber-500 focus-visible:ring-amber-500',
  },
  sm: { 
    class: 'text-xs px-2 py-1.5 min-h-[60px]',
    rows: 2,
  },
  lg: { 
    class: 'text-base px-4 py-3 min-h-[120px]',
    rows: 5,
  },
}

/** 최종 바인딩 */
const mapped = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  const finalClass = [preset?.class, props.class].filter(Boolean).join(' ')
  const finalRows = props.rows ?? preset?.rows ?? 3
  
  return {
    placeholder: props.placeholder,
    disabled: props.disabled,
    readonly: props.readonly,
    required: props.required,
    name: props.name,
    id: props.id,
    rows: finalRows,
    class: finalClass,
  }
})

const handleInput = (value: string | number) => {
  const strValue = String(value)
  emit('update:modelValue', strValue)
  emit('change', strValue)
}
</script>

<template>
  <Textarea 
    v-bind="mapped" 
    :model-value="modelValue as string" 
    @update:model-value="handleInput"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  />
</template>
